/* eslint-disable @typescript-eslint/no-explicit-any */
import { type SetStateAction, useMemo, useState } from "react";

import Avatar from "@/design/components/Avatar";
import Search from "@/design/components/Search";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, { type IValkyrieIcon } from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
} from "@mui/material";

export default function FilterDropdown({
  id,
  label,
  options = [],
  defaultOptions = [],
  searchKeys,
  getOptionLabel,
  getOptionLabelSecondary,
  getOptionValue,
  getAvatarIcon,
  value,
  setValue,
  icon,
  noAvatar,
}: {
  id: string;
  label?: string;
  options: object[];
  defaultOptions?: { label: string; value: string }[];
  searchKeys: string[];
  getOptionLabel: (object: object) => string;
  getOptionLabelSecondary?: (object: object) => string;
  getOptionValue: (object: object) => string;
  getAvatarIcon: (object: object) => IValkyrieIcon;
  value: string;
  setValue: React.ActionDispatch<SetStateAction<any>>;
  icon: IValkyrieIcon;
  noAvatar?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [needle, setNeedle] = useState("");

  const open = Boolean(anchorEl);

  const results = useSearch(options ?? [], needle, searchKeys);

  const activeOption = useMemo(
    () => [...defaultOptions, ...options].find((option) => getOptionValue(option) === value) ?? defaultOptions?.[0],
    [defaultOptions, getOptionValue, options, value],
  );

  const isDefaultOption = useMemo(
    () => !!defaultOptions.find((option) => getOptionValue(option) === value),
    [defaultOptions, getOptionValue, value],
  );

  function closeFilter() {
    setAnchorEl(undefined);
    setNeedle("");
  }

  return (
    <>
      <Button color="inherit" id={id} onClick={(event) => setAnchorEl(event.currentTarget)}>
        {!value || noAvatar || isDefaultOption ? (
          <Stack
            direction="row"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Valkyrie icon={icon} />
            <Box>
              {label ? `${label}: ` : ""}
              {getOptionLabel(activeOption)}
            </Box>
          </Stack>
        ) : (
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            <Avatar text={getOptionLabel(activeOption)} icon={getAvatarIcon?.(activeOption) ?? undefined} px={24} />
            <Box>
              {label ? `${label}: ` : ""}
              {getOptionLabel(activeOption)}
            </Box>
          </Stack>
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => closeFilter()}
        slotProps={{
          paper: {
            elevation: 1,
            sx: {
              border: "1px solid",
              borderColor: "divider",
              minWidth: 300,
            },
          },
        }}
      >
        {(searchKeys?.length ?? 0) >= 1 && (
          <Box sx={{ px: 1, pt: 1, position: "sticky", top: 0, zIndex: 100 }}>
            <Search blur id="search-accounts" accessKey="q" value={needle} onChange={setNeedle} />
          </Box>
        )}
        <List sx={{ p: 1, minWidth: 200 }}>
          {defaultOptions.map((option, optionKey) => (
            <ListItem disablePadding key={optionKey} sx={{ minHeight: 40 }}>
              <ListItemButton
                onClick={() => {
                  setValue(getOptionValue(option));
                  closeFilter();
                }}
                selected={value === option.value}
                sx={{ minHeight: 40 }}
              >
                <ListItemText primary={getOptionLabel(option)} />
              </ListItemButton>
            </ListItem>
          ))}

          {results.map((option: object, optionKey: number) => (
            <ListItem key={optionKey} disablePadding sx={{ minHeight: 40 }}>
              <ListItemButton
                sx={{ minHeight: 40 }}
                onClick={() => {
                  setValue(getOptionValue(option));
                  closeFilter();
                }}
                selected={value === getOptionValue(option)}
              >
                <ListItemAvatar sx={{ minWidth: 34 }}>
                  <Avatar text={getOptionLabel(option)} icon={getAvatarIcon?.(option) ?? undefined} px={24} />
                </ListItemAvatar>
                <ListItemText
                  primary={getOptionLabel(option)}
                  secondary={getOptionLabelSecondary?.(option)}
                  sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "baseline" }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
}
